import type * as THREE from 'three'
import type { GSplatPlacement } from './GSplatPlacement'

export interface GSplatLayerCamera {
  readonly camera: THREE.Camera
}

export interface GSplatLayerCulledInstances {
  readonly opaque: unknown[]
  readonly transparent: unknown[]
}

/**
 * Layer - 简化的 Splat 分组系统
 *
 * @remarks
 * 对应 PlayCanvas 的 Layer 类（简化版）
 * 移除了完整的 ECS 层系统，仅保留 Splat 分组管理
 *
 * 关键差异：
 * - 移除阴影投射支持（gsplatShadowCasters）
 * - 移除渲染顺序、裁剪等高级功能
 * - 简化为纯粹的 Placement 容器
 */
export class Layer<
  TPlacement extends object = GSplatPlacement,
  TDrawObject extends object = object,
> {
  /**
   * Layer 名称（用于查找和调试）
   */
  name: string

  /**
   * 是否启用此 Layer
   * @default true
   */
  private enabledValue = true

  /** Layer topology 变化标记。 */
  private compositionDirtyValue = false

  onEnable?: () => void
  onDisable?: () => void

  /**
   * Layer ID（用于相机过滤）
   */
  id: number

  /**
   * 此 Layer 中的 Splat Placements
   */
  gsplatPlacements: TPlacement[] = []

  /**
   * Placement 集合（用于快速查找）
   * @internal
   */
  gsplatPlacementsSet: Set<TPlacement> = new Set()

  /** 参与 shadow submission 的 Placement，保持插入顺序。 */
  gsplatShadowCasters: TPlacement[] = []

  /** shadow caster identity 集合。 */
  gsplatShadowCastersSet: Set<TPlacement> = new Set()

  /**
   * Placements 是否已修改（需要 Director 更新）
   * @internal
   */
  gsplatPlacementsDirty: boolean = true

  /** 普通 scene submission 的 draw objects，保持插入顺序。 */
  meshInstances: TDrawObject[] = []
  private readonly meshInstancesSet = new Set<TDrawObject>()

  /** shadow submission 的 draw objects，独立于 gsplat Placement caster 集合。 */
  shadowCasters: TDrawObject[] = []
  private readonly shadowCastersSet = new Set<TDrawObject>()

  /** 与上游 Layer 一致的 camera 插入顺序与 identity 集合。 */
  cameras: GSplatLayerCamera[] = []
  camerasSet = new Set<THREE.Camera>()

  /**
   * 上游默认以 Camera identity 保存 culled list；本引擎允许同一 Camera 被多个 View 复用，
   * 因此宿主帧边可以传入独立 owner，把 viewport/layer policy 相关结果隔离开。
   */
  private readonly visibleInstances = new WeakMap<
    object,
    { readonly camera: THREE.Camera; readonly instances: GSplatLayerCulledInstances }
  >()

  // ==================== 全局 ID 分配 ====================
  private static nextId = 0

  /**
   * 创建新的 Layer
   *
   * @param name - Layer 名称
   */
  constructor(name: string) {
    this.name = name
    this.id = Layer.nextId++
  }

  set enabled(value: boolean) {
    if (value === this.enabledValue) return
    this.enabledValue = value
    this.compositionDirtyValue = true
    this.gsplatPlacementsDirty = true
    if (value) this.onEnable?.()
    else this.onDisable?.()
  }

  get enabled(): boolean {
    return this.enabledValue
  }

  get compositionDirty(): boolean {
    return this.compositionDirtyValue
  }

  clearCompositionDirty(): void {
    this.compositionDirtyValue = false
  }

  // ==================== Placement 管理 ====================

  /**
   * 添加 Splat Placement 到此 Layer
   *
   * @param placement - GSplat Placement
   */
  addGSplatPlacement(placement: TPlacement): void {
    if (!this.gsplatPlacementsSet.has(placement)) {
      this.gsplatPlacements.push(placement)
      this.gsplatPlacementsSet.add(placement)
      this.markPlacementMembershipDirty()
    }
  }

  /**
   * 从此 Layer 移除 Splat Placement
   *
   * @param placement - GSplat Placement
   */
  removeGSplatPlacement(placement: TPlacement): void {
    const index = this.gsplatPlacements.indexOf(placement)
    if (index < 0) return
    this.gsplatPlacements.splice(index, 1)
    this.gsplatPlacementsSet.delete(placement)
    this.markPlacementMembershipDirty()
  }

  addGSplatShadowCaster(placement: TPlacement): void {
    if (this.gsplatShadowCastersSet.has(placement)) return
    this.gsplatShadowCasters.push(placement)
    this.gsplatShadowCastersSet.add(placement)
    this.markPlacementMembershipDirty()
  }

  removeGSplatShadowCaster(placement: TPlacement): void {
    const index = this.gsplatShadowCasters.indexOf(placement)
    if (index < 0) return
    this.gsplatShadowCasters.splice(index, 1)
    this.gsplatShadowCastersSet.delete(placement)
    this.markPlacementMembershipDirty()
  }

  /**
   * 清空此 Layer 的所有 Placements
   */
  clearGSplatPlacements(): void {
    if (this.gsplatPlacements.length > 0) {
      this.gsplatPlacements.length = 0
      this.gsplatPlacementsSet.clear()
      this.markPlacementMembershipDirty()
    }
  }

  clearGSplatShadowCasters(): void {
    if (this.gsplatShadowCasters.length === 0) return
    this.gsplatShadowCasters.length = 0
    this.gsplatShadowCastersSet.clear()
    this.markPlacementMembershipDirty()
  }

  addMeshInstances(instances: readonly TDrawObject[], skipShadowCasters = false): void {
    let changed = false
    for (const instance of instances) {
      if (!this.meshInstancesSet.has(instance)) {
        this.meshInstancesSet.add(instance)
        this.meshInstances.push(instance)
        changed = true
      }
      if (!skipShadowCasters) this.addShadowCasters([instance])
    }
    if (changed) this.compositionDirtyValue = true
  }

  removeMeshInstances(instances: readonly TDrawObject[], skipShadowCasters = false): void {
    let changed = false
    for (const instance of instances) {
      if (this.meshInstancesSet.delete(instance)) {
        const index = this.meshInstances.indexOf(instance)
        if (index >= 0) this.meshInstances.splice(index, 1)
        changed = true
      }
      if (!skipShadowCasters) this.removeShadowCasters([instance])
    }
    if (changed) this.compositionDirtyValue = true
  }

  addShadowCasters(instances: readonly TDrawObject[]): void {
    let changed = false
    for (const instance of instances) {
      if (this.shadowCastersSet.has(instance)) continue
      this.shadowCastersSet.add(instance)
      this.shadowCasters.push(instance)
      changed = true
    }
    if (changed) this.compositionDirtyValue = true
  }

  removeShadowCasters(instances: readonly TDrawObject[]): void {
    let changed = false
    for (const instance of instances) {
      if (!this.shadowCastersSet.delete(instance)) continue
      const index = this.shadowCasters.indexOf(instance)
      if (index >= 0) this.shadowCasters.splice(index, 1)
      changed = true
    }
    if (changed) this.compositionDirtyValue = true
  }

  /**
   * 检查是否包含指定 Placement。
   *
   * @param placement - 目标 GSplatPlacement
   * @returns 已包含返回 true
   */
  hasGSplatPlacement(placement: TPlacement): boolean {
    return this.gsplatPlacementsSet.has(placement)
  }

  addCamera(camera: GSplatLayerCamera): void {
    if (this.camerasSet.has(camera.camera)) return
    this.camerasSet.add(camera.camera)
    this.cameras.push(camera)
    this.compositionDirtyValue = true
  }

  removeCamera(camera: GSplatLayerCamera): void {
    if (!this.camerasSet.has(camera.camera)) return
    const index = this.cameras.indexOf(camera)
    if (index < 0) return
    this.cameras.splice(index, 1)
    this.camerasSet.delete(camera.camera)
    this.compositionDirtyValue = true
  }

  clearCameras(): void {
    if (this.cameras.length === 0) return
    this.cameras.length = 0
    this.camerasSet.clear()
    this.compositionDirtyValue = true
  }

  getCulledInstances(camera: THREE.Camera, owner: object = camera): GSplatLayerCulledInstances {
    let entry = this.visibleInstances.get(owner)
    if (!entry || entry.camera !== camera) {
      entry = { camera, instances: { opaque: [], transparent: [] } }
      this.visibleInstances.set(owner, entry)
    }
    return entry.instances
  }

  private markPlacementMembershipDirty(): void {
    this.gsplatPlacementsDirty = true
    this.compositionDirtyValue = true
  }
}
